import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './RuntimeShield.css';

import workspaceVisibilityImg from '../../assets/runtime/runtime_visbility.png';
import gatewayImg from '../../assets/runtime/gateway.png';
import discoveryImg from '../../assets/runtime/discovery.png';
import preventionImg from '../../assets/runtime/prevention.png';
import detectionImg from '../../assets/runtime/detection.png';
import runtimePolicyImg from '../../assets/runtime/runtime_policy.png';
import logoRotate from '../../assets/movie/logo_rotate_large_v2.gif';

const RuntimeShield = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const sectionIds = ['runtime-hero', 'section-integration', 'section-discovery', 'section-visibility', 'section-prevention', 'section-detection', 'section-policy', 'section-cta'];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getScrollTarget = (section) => {
    const title = section.querySelector('h2, .runtime-cta-btn, p');
    const target = title || section;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    return target.getBoundingClientRect().top + window.scrollY - navH - 40;
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: getScrollTarget(el), behavior: 'smooth' });
    }
  };

  const scrollToSection = useCallback((direction) => {
    const currentScroll = window.scrollY;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    const positions = sectionIds.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const title = el.querySelector('h2, .runtime-cta-btn, p');
      const target = title || el;
      return { top: target.getBoundingClientRect().top + window.scrollY, id, scrollTo: target.getBoundingClientRect().top + window.scrollY - navH - 40 };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = positions.find(p => p.scrollTo > currentScroll + 10);
      if (next) window.scrollTo({ top: next.scrollTo, behavior: 'smooth' });
    } else {
      const prev = [...positions].reverse().find(p => p.scrollTo < currentScroll - 10);
      if (prev) window.scrollTo({ top: prev.scrollTo, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>
      <section id="runtime-hero" className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <img src={logoRotate} alt="Bltz AI" className="runtime-hero-logo" />
        <h1 className="hero-page-title">Runtime Shield</h1>
      </section>

      <section className="runtime-content">
        <div className="runtime-layout">
          <h2 className="runtime-heading">Runtime Visibility and Protection</h2>
          <p className="runtime-description">
            Bltz enables complete visibility and protection for AI in production, discovering and monitoring all AI traffic at runtime while stopping prompt attacks, data leaks, and unsafe behavior with built-in AI governance and self-healing remediation.
          </p>

          <div className="runtime-cta" style={{ marginTop: 0, marginBottom: 20 }}>
            <button className="runtime-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo &rarr;
            </button>
          </div>

          {/* 4 Feature Boxes */}}
          <div className="runtime-features">
            <div className="runtime-feature-box">
              <div className="runtime-feature-header">
                <h3 className="runtime-feature-title">One-Stop Integration</h3>
              </div>
              <p className="runtime-feature-desc">Single integration point for all AI traffic monitoring and protection.</p>
              <div className="runtime-feature-arrow" onClick={() => scrollToId('section-integration')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="runtime-feature-box">
              <div className="runtime-feature-header">
                <h3 className="runtime-feature-title">Discover Shadow AI</h3>
              </div>
              <p className="runtime-feature-desc">Identify unauthorized AI usage across your organization in real time.</p>
              <div className="runtime-feature-arrow" onClick={() => scrollToId('section-discovery')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="runtime-feature-box">
              <div className="runtime-feature-header">
                <h3 className="runtime-feature-title">Prevent Unauthorized Access</h3>
              </div>
              <p className="runtime-feature-desc">Block unauthorized AI model access and enforce governance policies.</p>
              <div className="runtime-feature-arrow" onClick={() => scrollToId('section-prevention')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="runtime-feature-box">
              <div className="runtime-feature-header">
                <h3 className="runtime-feature-title">Detect &amp; Remediate Data Loss</h3>
              </div>
              <p className="runtime-feature-desc">Automatically detect and prevent sensitive data exfiltration through AI channels.</p>
              <div className="runtime-feature-arrow" onClick={() => scrollToId('section-detection')}>
                <span>&#8595;</span>
              </div>
            </div>
          </div>

          {/* One-Stop Integration Section */}
          <div id="section-integration" className="runtime-gateway-section runtime-section-light">
            <h2 className="runtime-gateway-title">One-Stop Integration</h2>
            <p className="runtime-gateway-desc">
              A single gateway that discovers and monitors all networked AI activity—including Agents, MCP servers, LLMs, chatbots, and other AI-powered applications—providing unified visibility and control across your entire AI ecosystem.
            </p>
            <div className="runtime-gateway-img-wrap">
              <img src={gatewayImg} alt="AI Gateway Integration" className="runtime-gateway-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Discover Shadow AI Section */}
          <div id="section-discovery" className="runtime-gateway-section">
            <h2 className="runtime-gateway-title">Discover Shadow AI</h2>
            <p className="runtime-gateway-desc">
              Comprehensive discovery policies that detect all AI activity across Agents, MCP servers, LLMs, chatbots, and custom applications—eliminating the need for multiple monitoring tools with a single, unified layer of visibility.
            </p>
            <div className="runtime-gateway-img-wrap">
              <img src={discoveryImg} alt="Shadow AI Discovery" className="runtime-gateway-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Visibility Dashboard Section */}
          <div id="section-visibility" className="runtime-gateway-section runtime-section-light">
            <div className="runtime-cta" style={{ marginTop: 0, marginBottom: 24 }}>
              <button className="runtime-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
                Get a Live Demo &rarr;
              </button>
            </div>
            <p className="runtime-gateway-desc">
              Comprehensive visibility dashboard with real-time risk detection, threat monitoring, and AI governance analytics across your entire organization with centralized one-stop integration.
            </p>
            <div className="runtime-visibility-img-wrap">
              <img src={workspaceVisibilityImg} alt="Workspace Visibility" className="runtime-visibility-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Prevention Section */}
          <div id="section-prevention" className="runtime-gateway-section">
            <h2 className="runtime-gateway-title">Prevention</h2>
            <p className="runtime-gateway-desc">
              Monitor a wide range of AI threats at runtime and proactively stop them from reaching your production infrastructure and network.
            </p>
            <div className="runtime-gateway-img-wrap">
              <img src={preventionImg} alt="AI Threat Prevention" className="runtime-gateway-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Detection & Remediation Section */}
          <div id="section-detection" className="runtime-gateway-section runtime-section-light">
            <h2 className="runtime-gateway-title">Detection &amp; Remediation</h2>
            <p className="runtime-gateway-desc">
              Detect and remediate network attacks, prompt injection, jailbreak attempts, access attacks, and a wide range of other AI threats across your entire network in real time with real-time controls aligned to industry-leading compliance frameworks including ISO 42001, NIST AI RMF, OWASP Top 10 for LLMs, and MITRE ATLAS.
            </p>
            <div className="runtime-cta" style={{ marginTop: 0, marginBottom: 24 }}>
              <button className="runtime-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
                Get a Live Demo &rarr;
              </button>
            </div>
            <div className="runtime-gateway-img-wrap">
              <img src={detectionImg} alt="Detection and Remediation" className="runtime-gateway-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Runtime Policy Section */}
          <div id="section-policy" className="runtime-gateway-section">
            <h2 className="runtime-gateway-title">Runtime Policy</h2>
            <p className="runtime-gateway-desc">
              The most comprehensive AI runtime threat detection policy library, arming your runtime agent with 24/7 autonomous protection across every AI endpoint. Do not miss any attempt to compromise your AI infrastructure and valuable data.
            </p>
            <div className="runtime-gateway-img-wrap">
              <img src={runtimePolicyImg} alt="Runtime Policy Library" className="runtime-gateway-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* CTA */}
          <div id="section-cta" className="runtime-cta">
            <button className="runtime-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              See Runtime Shield in Action &rarr;
            </button>
          </div>

          <span className="runtime-api-link" onClick={() => { navigate('/'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer' }}>
            ← Back to Home
          </span>
        </div>
      </section>
    </>
  );
};

export default RuntimeShield;
